import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import SEO from "../components/SEO";
import {
  ArrowRight,
  CheckCircle,
  Download,
  FileText,
  Lock,
  Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const leadMagnetsData = {
  "business-automation-guide": {
    title: "Free Business Automation Guide",
    subtitle: "The Complete Framework for Automating Your Business",
    description: "Learn how to identify, prioritize, and implement automation in your business to save time, reduce errors, and scale efficiently.",
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200",
    benefits: [
      "Identify your highest-impact automation opportunities",
      "Choose the right tools for your business size and budget",
      "Create an implementation roadmap you can follow",
      "Avoid common automation mistakes that waste time and money",
      "Calculate ROI before you invest in automation",
    ],
    sections: [
      { title: "Chapter 1: The Automation Mindset", description: "Shift from task-thinking to system-thinking" },
      { title: "Chapter 2: Automation Audit", description: "Identify what to automate first" },
      { title: "Chapter 3: Tool Selection", description: "Match tools to your needs and budget" },
      { title: "Chapter 4: Implementation", description: "Step-by-step automation rollout" },
      { title: "Chapter 5: Optimization", description: "Measure and improve your automations" },
    ],
    content: `
# The Complete Business Automation Guide

## Introduction: Why Automation Matters

In today's competitive landscape, automation isn't a luxury—it's a necessity. Businesses that systematically automate their operations enjoy:
- 40-60% reduction in time spent on repetitive tasks
- Significant decrease in human errors
- Ability to scale without proportionally increasing headcount
- Consistent customer experiences 24/7

This guide will help you identify, prioritize, and implement automation in your business.

---

## Chapter 1: The Automation Mindset

### Thinking in Systems

The first step to successful automation is shifting from task-thinking to system-thinking. Instead of asking "How do I complete this task?", ask "How do I build a system that completes this task automatically?"

### The 4D Framework

When evaluating any process for automation, consider the 4Ds:
1. **Delete**: Can this task be eliminated entirely?
2. **Delegate**: Can someone else (or something else) do this?
3. **Defer**: Can this be batched or scheduled differently?
4. **Do**: Only manually do what absolutely requires human judgment

### Automation ROI Calculation

Before automating anything, calculate potential ROI:
- Time currently spent per week × hourly cost = current cost
- One-time setup cost + ongoing tool costs = automation cost
- Current cost - automation cost = savings
- Payback period = setup cost ÷ monthly savings

---

## Chapter 2: Automation Audit

### Process Mapping

Document your current processes:
1. List all recurring tasks in your business
2. Note frequency (daily, weekly, monthly)
3. Estimate time spent on each
4. Identify dependencies and triggers
5. Rate complexity (simple, moderate, complex)

### Prioritization Matrix

Score each process on two dimensions:
- Impact (time saved × frequency × error reduction)
- Difficulty (technical complexity × change management)

Start with high-impact, low-difficulty items.

### Common Automation Opportunities

**Marketing & Sales:**
- Lead capture and initial follow-up
- Email nurture sequences
- Social media scheduling
- CRM data entry
- Report generation

**Operations:**
- Invoice generation
- Payment reminders
- Appointment scheduling
- Customer onboarding
- Support ticket routing

**Internal:**
- Employee onboarding checklists
- Time-off request workflows
- Expense approvals
- Meeting scheduling
- Document filing

---

## Chapter 3: Tool Selection

### Automation Platforms by Category

**All-in-One Platforms (Best for most SMBs):**
- GoHighLevel: Marketing, CRM, automation
- HubSpot: Marketing, sales, service
- ActiveCampaign: Email, CRM, automation

**Integration Platforms:**
- Zapier: 5000+ app integrations
- Make (Integromat): Complex workflows
- n8n: Self-hosted option

**Specialized Tools:**
- Calendly: Scheduling
- DocuSign: Document signing
- QuickBooks: Accounting automation

### Selection Criteria

When choosing tools, evaluate:
1. Does it integrate with your existing stack?
2. What's the learning curve for your team?
3. Total cost of ownership (not just subscription)
4. Vendor stability and support quality
5. Data portability if you need to switch

---

## Chapter 4: Implementation

### The Phased Approach

**Phase 1: Foundation (Week 1-2)**
- Select and set up core platform
- Import existing data
- Configure basic settings
- Train key team members

**Phase 2: Quick Wins (Week 3-4)**
- Implement 2-3 simple automations
- Measure results
- Document learnings
- Build team confidence

**Phase 3: Scale (Month 2+)**
- Add more complex automations
- Integrate additional tools
- Optimize based on data
- Expand to new areas

### Change Management

Automation fails when people don't adopt it. Ensure success by:
- Involving team in tool selection
- Explaining benefits to each role
- Providing adequate training
- Creating feedback channels
- Celebrating wins publicly

---

## Chapter 5: Optimization

### Metrics to Track

- Time saved per week
- Error rates (before vs. after)
- Process completion rates
- Customer satisfaction impact
- Cost savings

### Continuous Improvement

Schedule monthly automation reviews:
1. What's working well?
2. What's not being used?
3. What new opportunities exist?
4. What needs adjustment?

### Scaling Your Automation

As you mature, consider:
- More sophisticated triggers and conditions
- Multi-step workflows
- AI-powered decision points
- Custom integrations via APIs
- Dedicated automation resource

---

## Next Steps

Ready to implement automation in your business? Here's how to proceed:

1. **Download the Process Audit Template** (included below)
2. **Complete your automation audit** using Chapter 2's framework
3. **Prioritize your opportunities** with the scoring matrix
4. **Start with one quick win** to build momentum

Need help? [Book a free consultation](/contact) with our team to discuss your automation strategy.

---

*This guide is provided by OptimaGrowth. For personalized automation consulting, visit [optimagrowth.agency](/contact).*
    `,
  },
  "ai-growth-blueprint": {
    title: "AI-Assisted Growth Blueprint",
    subtitle: "A Strategic Framework for Leveraging AI in Your Business",
    description: "Cut through the AI hype and learn practical applications that drive real business growth.",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200",
    benefits: [
      "Understand where AI adds real value (and where it doesn't)",
      "Identify high-impact AI opportunities for your business",
      "Select the right AI tools for your needs",
      "Implement AI without disrupting your operations",
      "Measure ROI from your AI investments",
    ],
    sections: [
      { title: "Section 1: AI Fundamentals", description: "Understanding AI capabilities and limitations" },
      { title: "Section 2: Use Case Mapping", description: "Identifying AI opportunities by function" },
      { title: "Section 3: Tool Selection", description: "Choosing the right AI tools" },
      { title: "Section 4: Implementation", description: "Rolling out AI in your organization" },
      { title: "Section 5: Optimization", description: "Measuring and improving AI impact" },
    ],
    content: `
# AI-Assisted Growth Blueprint

## Introduction: AI Beyond the Hype

Artificial Intelligence is transforming business—but not in the way most headlines suggest. The real AI revolution isn't about replacing humans or achieving superintelligence. It's about practical tools that help businesses operate more efficiently and compete more effectively.

This blueprint provides a strategic framework for leveraging AI in your business.

---

## Section 1: AI Fundamentals

### What AI Actually Does

Modern AI excels at:
- **Pattern Recognition**: Finding patterns in large datasets
- **Prediction**: Forecasting based on historical data
- **Automation**: Handling repetitive cognitive tasks
- **Generation**: Creating content, images, code

AI struggles with:
- Novel situations outside its training
- Complex reasoning and judgment
- Understanding context and nuance
- Creative leaps and innovation

### AI Categories for Business

**Narrow AI (Available Now):**
- Language models (ChatGPT, Claude)
- Image generation (DALL-E, Midjourney)
- Predictive analytics
- Process automation

**Applied AI Tools:**
- Marketing optimization
- Customer service chatbots
- Sales intelligence
- Content creation assistance

---

## Section 2: Use Case Mapping

### Marketing Applications

**High Impact:**
- Content draft generation
- Email optimization
- Ad creative testing
- SEO research acceleration

**Medium Impact:**
- Social media management
- Customer segmentation
- Campaign reporting
- Competitive analysis

### Sales Applications

**High Impact:**
- Lead scoring and prioritization
- Meeting scheduling automation
- Proposal generation assistance
- CRM data enrichment

**Medium Impact:**
- Call transcription and analysis
- Objection handling suggestions
- Territory optimization
- Sales forecasting

### Operations Applications

**High Impact:**
- Document processing
- Customer inquiry routing
- Workflow automation
- Data entry elimination

**Medium Impact:**
- Scheduling optimization
- Inventory prediction
- Quality assurance
- Report generation

---

## Section 3: Tool Selection

### Evaluation Framework

For each AI tool, assess:
1. **Problem Fit**: Does it address a real need?
2. **Quality**: How good are the outputs?
3. **Integration**: Does it work with existing tools?
4. **Learning Curve**: How long to get value?
5. **Cost**: What's the total cost of ownership?

### Recommended Starting Points

**For Content:**
- Claude or ChatGPT for writing assistance
- Jasper for marketing-specific content
- Grammarly for editing

**For Marketing:**
- Platform-native AI (Meta, Google)
- SEO tools with AI (Surfer, Clearscope)
- Analytics AI (GA4, Mixpanel)

**For Operations:**
- Zapier/Make with AI features
- Documentation AI (Notion AI)
- Meeting assistants (Otter.ai)

---

## Section 4: Implementation

### The Pilot Approach

Start small:
1. Choose one use case with clear metrics
2. Select appropriate tool
3. Train small team
4. Run 30-day pilot
5. Measure results
6. Decide: expand, pivot, or stop

### Change Management for AI

Successful AI adoption requires:
- Clear communication about AI's role
- Training on effective AI use
- Guidelines for quality control
- Feedback mechanisms
- Gradual expansion

### Quality Control

Never deploy AI outputs without review:
- Establish review checkpoints
- Create output quality standards
- Train team on AI limitations
- Document and share learnings

---

## Section 5: Optimization

### Measuring AI Impact

Track:
- Time saved per task
- Quality of outputs
- User adoption rates
- Business impact metrics
- Cost per output

### Continuous Improvement

Monthly reviews should address:
- What AI applications are delivering value?
- Where is quality falling short?
- What new opportunities exist?
- What training is needed?

### Scaling AI Use

As you mature:
- Expand to new use cases
- Integrate AI more deeply
- Develop internal expertise
- Consider custom solutions

---

## Next Steps

1. **Complete the AI Opportunity Assessment** (framework in Section 2)
2. **Prioritize your top 3 opportunities**
3. **Select tools for your first pilot**
4. **Run a 30-day test**
5. **Review and expand**

Need help developing your AI strategy? [Book a consultation](/contact) with our team.

---

*This blueprint is provided by OptimaGrowth. For AI implementation consulting, visit [optimagrowth.agency](/contact).*
    `,
  },
  "lead-generation-checklist": {
    title: "Lead Generation Systems Checklist",
    subtitle: "Everything You Need for Consistent Lead Flow",
    description: "A comprehensive checklist for building lead generation systems that deliver predictable results.",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200",
    benefits: [
      "Audit your current lead generation capabilities",
      "Identify gaps in your lead generation system",
      "Prioritize improvements for maximum impact",
      "Build a consistent lead flow",
      "Track and optimize your results",
    ],
    sections: [
      { title: "Part 1: Foundation", description: "Core elements every system needs" },
      { title: "Part 2: Channels", description: "Lead generation channel checklist" },
      { title: "Part 3: Capture", description: "Converting visitors to leads" },
      { title: "Part 4: Qualification", description: "Separating good from bad leads" },
      { title: "Part 5: Nurture", description: "Moving leads toward purchase" },
    ],
    content: `
# Lead Generation Systems Checklist

## Introduction

Consistent lead flow doesn't happen by accident. It's the result of systematic attention to every stage of the lead generation process. This checklist helps you audit, build, and optimize your lead generation system.

---

## Part 1: Foundation Checklist

### Ideal Customer Profile (ICP)
☐ Demographics defined (industry, size, location)
☐ Psychographics understood (goals, challenges, values)
☐ Buying triggers identified
☐ Decision-making process mapped
☐ Common objections documented

### Value Proposition
☐ Clear statement of what you do
☐ Specific outcomes you deliver
☐ Differentiation from competitors
☐ Proof points (case studies, testimonials)
☐ Risk reversal (guarantees, trials)

### Tracking & Analytics
☐ Website analytics installed
☐ Goal tracking configured
☐ UTM parameter system in place
☐ CRM tracking integrated
☐ Attribution model defined

---

## Part 2: Channel Checklist

### Organic Channels
☐ SEO strategy in place
☐ Content calendar active
☐ Social media presence optimized
☐ Google Business Profile claimed
☐ Industry listings updated

### Paid Channels
☐ Google Ads account set up
☐ Meta Ads account configured
☐ LinkedIn Ads (if B2B) considered
☐ Retargeting pixels installed
☐ Conversion tracking verified

### Referral Channels
☐ Customer referral program
☐ Partner referral program
☐ Affiliate program (if applicable)
☐ Referral tracking system
☐ Incentive structure defined

### Outbound Channels
☐ Email outreach sequences
☐ LinkedIn outreach (if B2B)
☐ Cold calling scripts (if applicable)
☐ Account-based marketing strategy
☐ Event networking plan

---

## Part 3: Capture Checklist

### Lead Magnets
☐ Primary lead magnet created
☐ Secondary lead magnets for different stages
☐ Landing pages optimized
☐ Forms tested and working
☐ Delivery automation set up

### Website Optimization
☐ Clear CTAs on every page
☐ Exit-intent popups configured
☐ Chat widget installed
☐ Contact page optimized
☐ Mobile experience verified

### Forms
☐ Field count minimized
☐ Progressive profiling enabled
☐ Auto-fill enabled
☐ Thank you pages configured
☐ Confirmation emails set up

---

## Part 4: Qualification Checklist

### Lead Scoring
☐ Demographic scoring criteria
☐ Behavioral scoring criteria
☐ Score thresholds defined
☐ Scoring automation active
☐ Regular score calibration

### Lead Routing
☐ Routing rules defined
☐ Assignment automation
☐ Response time standards
☐ Escalation procedures
☐ Coverage during absences

### Data Enrichment
☐ Contact data enrichment
☐ Company data enrichment
☐ Social profile linking
☐ Data quality monitoring
☐ Duplicate management

---

## Part 5: Nurture Checklist

### Email Nurture
☐ Welcome sequence active
☐ Education sequence built
☐ Re-engagement sequence ready
☐ Segmentation in place
☐ Performance monitoring

### Multi-Channel Nurture
☐ Retargeting ads running
☐ Social engagement monitored
☐ SMS sequences (if appropriate)
☐ Direct mail (if appropriate)
☐ Event invitations automated

### Sales Handoff
☐ Handoff criteria defined
☐ Notification system working
☐ Context provided to sales
☐ Feedback loop established
☐ Win/loss analysis process

---

## Implementation Priority

### Start Here (Week 1-2)
1. Complete ICP documentation
2. Audit current lead tracking
3. Create/optimize primary lead magnet
4. Set up basic nurture sequence

### Quick Wins (Week 3-4)
1. Optimize website CTAs
2. Implement lead scoring
3. Launch retargeting
4. Add chat widget

### Build Out (Month 2+)
1. Expand content marketing
2. Add paid channels
3. Build referral program
4. Implement advanced automation

---

## Next Steps

1. **Print this checklist** and audit your current system
2. **Highlight gaps** where you're missing elements
3. **Prioritize** using the implementation guide above
4. **Take action** on your top 3 priorities this week

Need help building your lead generation system? [Book a free consultation](/contact) with our team.

---

*This checklist is provided by OptimaGrowth. For lead generation consulting, visit [optimagrowth.agency](/contact).*
    `,
  },
  "marketing-automation-playbook": {
    title: "Marketing Automation Playbook for SMEs",
    subtitle: "Practical Automation Strategies for Growing Businesses",
    description: "A hands-on playbook for implementing marketing automation that drives results without overwhelming your team.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200",
    benefits: [
      "Understand which automations matter most",
      "Choose the right platform for your needs",
      "Implement proven automation sequences",
      "Avoid common automation mistakes",
      "Scale your marketing without adding headcount",
    ],
    sections: [
      { title: "Chapter 1: Getting Started", description: "Building your automation foundation" },
      { title: "Chapter 2: Platform Selection", description: "Choosing the right tool for you" },
      { title: "Chapter 3: Essential Sequences", description: "The automations every business needs" },
      { title: "Chapter 4: Advanced Tactics", description: "Taking automation to the next level" },
      { title: "Chapter 5: Optimization", description: "Measuring and improving performance" },
    ],
    content: `
# Marketing Automation Playbook for SMEs

## Introduction

Marketing automation sounds complex, but it doesn't have to be. At its core, automation is about setting up systems that deliver the right message to the right person at the right time—without manual intervention.

This playbook provides practical, implementable strategies for growing businesses.

---

## Chapter 1: Getting Started

### The Automation Mindset

Before diving into tools, adopt the right mindset:
- **Think sequences, not campaigns**: Build once, run forever
- **Start simple**: One well-executed automation beats ten half-built ones
- **Focus on the customer**: Every automation should improve their experience
- **Measure everything**: You can't improve what you don't track

### Prerequisites for Automation

Before automating, ensure you have:
1. Clear understanding of your customer journey
2. CRM or database for contact management
3. Email sending capability
4. Basic tracking in place
5. Content to use in automations

### The Automation Audit

Document your current state:
- What manual touchpoints exist in your marketing?
- Where do leads fall through the cracks?
- What tasks consume the most time?
- Where are errors most common?

---

## Chapter 2: Platform Selection

### Platform Categories

**All-in-One (Best for most SMEs):**
- GoHighLevel: Most cost-effective all-in-one
- HubSpot: Enterprise features, higher price
- ActiveCampaign: Email-focused with CRM

**Email-Focused:**
- Mailchimp: Good for beginners
- Klaviyo: Great for e-commerce
- ConvertKit: Good for creators

**Enterprise:**
- Salesforce Marketing Cloud
- Marketo
- Pardot

### Selection Criteria

Evaluate based on:
1. Your current and future needs
2. Integration with existing tools
3. Total cost (including implementation)
4. Learning curve for your team
5. Quality of support

### Our Recommendation

For most SMEs, we recommend starting with GoHighLevel or ActiveCampaign:
- Comprehensive features at reasonable cost
- Manageable learning curve
- Good scalability
- Strong community support

---

## Chapter 3: Essential Sequences

### Welcome Sequence (Must Have)

**Trigger**: New subscriber or lead
**Goal**: Introduce your brand and build relationship

Email 1 (Immediate): Welcome + deliver promised resource
Email 2 (Day 2): Introduce yourself/company
Email 3 (Day 4): Share valuable content
Email 4 (Day 7): Social proof (testimonials/case studies)
Email 5 (Day 10): Soft CTA (book a call, buy, etc.)

### Lead Nurture Sequence (Must Have)

**Trigger**: Lead not yet ready to buy
**Goal**: Educate and build trust until ready

Week 1: Educational content related to their interest
Week 2: Problem agitation + solution introduction
Week 3: Case study or proof
Week 4: Objection handling
Week 5: Offer with deadline

### Re-Engagement Sequence (Must Have)

**Trigger**: No engagement for 30-60 days
**Goal**: Re-activate or clean list

Email 1: "We miss you" + value reminder
Email 2: Exclusive offer or content
Email 3: "Last chance" before removal
Email 4: Final notice + easy unsubscribe

### Post-Purchase Sequence (High Value)

**Trigger**: Purchase or signup
**Goal**: Ensure success and encourage advocacy

Email 1 (Immediate): Confirmation + next steps
Email 2 (Day 2): Getting started guide
Email 3 (Day 7): Check-in + helpful tips
Email 4 (Day 14): Request feedback
Email 5 (Day 30): Ask for review/referral

---

## Chapter 4: Advanced Tactics

### Behavioral Triggers

Go beyond time-based sequences:
- **Page visit triggers**: Send relevant content based on pages viewed
- **Cart abandonment**: Recover lost sales
- **Product usage**: Engage based on how they use your product
- **Inactivity**: Re-engage before they churn

### Lead Scoring

Prioritize your best leads:
- **Demographic score**: Fit with your ICP
- **Behavioral score**: Engagement with your content
- **Combined threshold**: When to alert sales

### Multi-Channel Automation

Extend beyond email:
- **SMS**: For urgent or time-sensitive messages
- **Retargeting ads**: Stay visible after email engagement
- **Direct mail**: For high-value prospects
- **Social touches**: Coordinate social engagement

---

## Chapter 5: Optimization

### Key Metrics

Track these for every automation:
- **Open rate**: Are subject lines working?
- **Click rate**: Is content compelling?
- **Conversion rate**: Are CTAs effective?
- **Unsubscribe rate**: Are you over-sending?
- **Revenue attributed**: What's the ROI?

### A/B Testing Framework

Test systematically:
1. Test one element at a time
2. Use meaningful sample sizes
3. Run tests for full cycles
4. Document learnings
5. Implement winners across sequences

### Regular Audits

Monthly automation health check:
- Are sequences still relevant?
- Are trigger conditions correct?
- Are there deliverability issues?
- What can be improved?

---

## Implementation Timeline

### Week 1: Foundation
- Select platform
- Set up account
- Import contacts
- Configure basic settings

### Week 2: First Automation
- Build welcome sequence
- Test thoroughly
- Launch to new leads

### Week 3-4: Core Sequences
- Build lead nurture sequence
- Build re-engagement sequence
- Begin optimization

### Month 2+: Expansion
- Add behavioral triggers
- Implement lead scoring
- Add channels (SMS, retargeting)
- Advanced optimization

---

## Next Steps

1. **Complete the automation audit** from Chapter 1
2. **Select your platform** using Chapter 2's criteria
3. **Build your welcome sequence** first
4. **Launch and learn** before adding complexity

Ready for expert help? [Book a free consultation](/contact) with our automation team.

---

*This playbook is provided by OptimaGrowth. For marketing automation consulting, visit [optimagrowth.agency](/contact).*
    `,
  },
};

const LeadMagnetPage = () => {
  const { slug } = useParams();
  const magnet = leadMagnetsData[slug];
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
  });

  if (!magnet) {
    return (
      <div className="pt-32 section" data-testid="magnet-not-found">
        <div className="container-custom text-center">
          <h1 className="font-heading text-3xl font-bold text-optima-dark mb-4">
            Resource Not Found
          </h1>
          <Link to="/resources" className="btn-primary">
            View All Resources
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/lead-magnet`, {
        ...formData,
        lead_magnet: slug,
      });
      toast.success(response.data.message);
      setIsUnlocked(true);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-32" data-testid={`lead-magnet-${slug}`}>
      <SEO 
        title={magnet ? magnet.title : "Free Resource"}
        description={magnet ? magnet.description : "Download free growth resources from OptimaGrowth."}
        keywords={magnet ? `free guide, ${magnet.title.toLowerCase()}, business growth, marketing resources` : "free marketing guide"}
      />
      {!isUnlocked ? (
        // Gated Content
        <section className="section" data-testid="gated-section">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="badge badge-cyan mb-4">Free Guide</span>
                <h1 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-4">
                  {magnet.title}
                </h1>
                <p className="text-xl text-slate-600 mb-6">
                  {magnet.subtitle}
                </p>
                <p className="text-slate-600 mb-8">
                  {magnet.description}
                </p>

                <div className="space-y-3 mb-8">
                  <h3 className="font-semibold text-optima-dark">What you'll learn:</h3>
                  {magnet.benefits.map((benefit, index) => (
                    <div key={`benefit-${benefit.substring(0, 30).replace(/\s/g, '-')}`} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-slate-50 rounded-xl p-6">
                  <h3 className="font-semibold text-optima-dark mb-4">What's Inside:</h3>
                  <div className="space-y-3">
                    {magnet.sections.map((section, index) => (
                      <div key={`section-${section.title.replace(/\s/g, '-')}`} className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-optima-blue flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium text-optima-dark">{section.title}</span>
                          <p className="text-sm text-slate-600">{section.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="bg-white rounded-2xl border border-slate-200 shadow-medium p-8">
                  <div className="flex items-center gap-2 text-optima-blue mb-4">
                    <Lock className="w-5 h-5" />
                    <span className="font-semibold">Unlock Free Access</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-optima-dark mb-2">
                    Get Instant Access
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Enter your details below to unlock this free resource.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        placeholder="Full Name *"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="input-field"
                        data-testid="magnet-name-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="input-field"
                        data-testid="magnet-email-input"
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Company Name (Optional)"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="input-field"
                        data-testid="magnet-company-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-optima-blue hover:bg-optima-blue-dark py-6"
                      data-testid="magnet-submit-btn"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Get Free Access
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      We respect your privacy. Unsubscribe anytime.
                    </p>
                  </form>
                </div>

                <img
                  src={magnet.image}
                  alt={magnet.title}
                  className="rounded-xl mt-8 shadow-soft"
                />
              </motion.div>
            </div>
          </div>
        </section>
      ) : (
        // Unlocked Content
        <section className="section" data-testid="unlocked-section">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8 flex items-center gap-4">
                  <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-green-800">Access Granted!</h3>
                    <p className="text-green-700">
                      Scroll down to read your guide. We've also sent a copy to your email.
                    </p>
                  </div>
                </div>

                <h1 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-4">
                  {magnet.title}
                </h1>
                <p className="text-xl text-slate-600 mb-8">
                  {magnet.subtitle}
                </p>

                <img
                  src={magnet.image}
                  alt={magnet.title}
                  className="w-full rounded-2xl mb-12"
                />

                <div className="prose max-w-none">
                  {magnet.content.split("\n").map((line, index) => {
                    const lineKey = `md-${index}-${line.substring(0, 16).replace(/\W/g, '')}`;
                    if (line.startsWith("# ")) {
                      return (
                        <h1 key={lineKey} className="font-heading text-3xl font-bold text-optima-dark mt-12 mb-6">
                          {line.replace("# ", "")}
                        </h1>
                      );
                    }
                    if (line.startsWith("## ")) {
                      return (
                        <h2 key={lineKey} className="font-heading text-2xl font-bold text-optima-dark mt-10 mb-4">
                          {line.replace("## ", "")}
                        </h2>
                      );
                    }
                    if (line.startsWith("### ")) {
                      return (
                        <h3 key={lineKey} className="font-heading text-xl font-semibold text-optima-dark mt-8 mb-3">
                          {line.replace("### ", "")}
                        </h3>
                      );
                    }
                    if (line.startsWith("- ") || line.startsWith("☐ ")) {
                      return (
                        <li key={lineKey} className="text-slate-700 ml-4">
                          {line.replace(/^[-☐] /, "")}
                        </li>
                      );
                    }
                    if (line.startsWith("**") && line.includes(":**")) {
                      const [label, ...rest] = line.split(":**");
                      return (
                        <p key={lineKey} className="text-slate-700">
                          <strong>{label.replace("**", "")}:</strong>{rest.join(":**")}
                        </p>
                      );
                    }
                    if (line.startsWith("---")) {
                      return <hr key={lineKey} className="my-8 border-slate-200" />;
                    }
                    if (line.trim()) {
                      return <p key={lineKey} className="text-slate-700 mb-4">{line}</p>;
                    }
                    return null;
                  })}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="magnet-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl font-bold text-white mb-6">
              Need Help Implementing These Strategies?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Book a free growth audit and let's discuss how to apply these insights to your specific situation.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="magnet-final-cta"
            >
              Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LeadMagnetPage;
